// src/workspace/paperAnalysis/paperEngine.js
// ─────────────────────────────────────────────────────────────────────────────
// Pure-JS research paper analysis engine.
// NO external AI dependency — structural intelligence via computation.
// AI agents AUGMENT the pipeline; the pipeline itself is the product.
// ─────────────────────────────────────────────────────────────────────────────

// ── PDF Loader (lazy) ─────────────────────────────────────────────────────────
let _pdf = null;
async function loadPdfJs() {
  if (_pdf) return _pdf;
  if (window.pdfjsLib) { _pdf = window.pdfjsLib; return _pdf; }
  await new Promise((res, rej) => {
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    s.onload = res;
    s.onerror = () => rej(new Error('PDF.js load failed'));
    document.head.appendChild(s);
  });
  window.pdfjsLib.GlobalWorkerOptions.workerSrc =
    'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
  _pdf = window.pdfjsLib;
  return _pdf;
}

export async function extractPdfText(file, onProg) {
  const lib = await loadPdfJs();
  const pdf = await lib.getDocument({ data: await file.arrayBuffer() }).promise;
  const pages = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    pages.push(content.items.map(it => it.str).join(' ').trim());
    onProg?.(Math.round((i / pdf.numPages) * 100));
    await new Promise(r => setTimeout(r, 0));
  }
  return { text: pages.join('\n\n'), numPages: pdf.numPages, pages };
}

export function readFileAsText(file) {
  return new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload = e => res(String(e.target.result || ''));
    fr.onerror = rej;
    fr.readAsText(file);
  });
}

// ── Section Structure Detection ───────────────────────────────────────────────
// Detects academic paper sections via heading patterns, numbering, and keywords.
const SECTION_KEYWORDS = {
  abstract:     /^(?:\d+[\.\)]?\s*)?abstract/i,
  introduction: /^(?:\d+[\.\)]?\s*)?introduction/i,
  related_work: /^(?:\d+[\.\)]?\s*)?(?:related\s+work|literature\s+review|background|prior\s+work)/i,
  methodology:  /^(?:\d+[\.\)]?\s*)?(?:method(?:ology|s)?|approach|framework|model|system\s+design|proposed\s+(?:method|approach|framework))/i,
  experiments:  /^(?:\d+[\.\)]?\s*)?(?:experiment(?:al|s)?|evaluation|empirical\s+(?:evaluation|study|results)|setup)/i,
  results:      /^(?:\d+[\.\)]?\s*)?(?:results?|findings|analysis(?:\s+of\s+results)?|performance)/i,
  discussion:   /^(?:\d+[\.\)]?\s*)?(?:discussion|implications|interpretation)/i,
  conclusion:   /^(?:\d+[\.\)]?\s*)?(?:conclusion(?:s)?|summary|future\s+work|concluding\s+remarks)/i,
  references:   /^(?:\d+[\.\)]?\s*)?(?:references?|bibliography|works\s+cited)/i,
  appendix:     /^(?:\d+[\.\)]?\s*)?(?:appendix|appendices|supplementary)/i,
};

const NUMBERED_HEADING = /^(\d+(?:\.\d+)*)\s*[.)\s]\s*(.+)/;
const ALL_CAPS_HEADING = /^[A-Z][A-Z\s:&,\-]{4,}$/;
const TITLE_CASE_HEADING = /^(?:[A-Z][a-z]+(?:\s+(?:and|the|of|in|for|with|a|an|to|on|at|by)\s+)?){2,}/;

export function detectSections(text) {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  const sections = [];
  let current = { type: 'preamble', title: 'Title & Authors', lines: [], startLine: 0 };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    let matched = null;

    // Check known section keywords
    for (const [type, rx] of Object.entries(SECTION_KEYWORDS)) {
      if (rx.test(line) && line.length < 80) {
        matched = { type, title: line };
        break;
      }
    }

    // Check numbered headings (e.g., "3.1 Feature Extraction")
    if (!matched && NUMBERED_HEADING.test(line) && line.length < 100) {
      const m = line.match(NUMBERED_HEADING);
      matched = { type: 'numbered', title: line, number: m[1], heading: m[2] };
    }

    // Check ALL CAPS headings
    if (!matched && ALL_CAPS_HEADING.test(line) && line.length < 60 && line.length > 3) {
      matched = { type: 'heading', title: line };
    }

    if (matched) {
      if (current.lines.length > 0 || current.type === 'preamble') {
        sections.push({ ...current, lineCount: current.lines.length });
      }
      current = { ...matched, lines: [], startLine: i };
    } else {
      current.lines.push(line);
    }
  }
  if (current.lines.length > 0) {
    sections.push({ ...current, lineCount: current.lines.length });
  }

  return sections.map(s => ({
    ...s,
    text: s.lines.join(' '),
    wordCount: s.lines.join(' ').split(/\s+/).filter(Boolean).length,
    charCount: s.lines.join('').length,
  }));
}

// ── Metadata Extraction ───────────────────────────────────────────────────────
export function extractMetadata(text, sections) {
  const preamble = sections.find(s => s.type === 'preamble');
  const abstractSection = sections.find(s => s.type === 'abstract');

  // Title: first non-empty line in preamble, usually the longest line in first 5 lines
  const firstLines = text.split('\n').map(l => l.trim()).filter(Boolean).slice(0, 8);
  const title = firstLines.reduce((best, line) => {
    if (line.length > best.length && line.length < 200 && !line.match(/^\d/)) return line;
    return best;
  }, firstLines[0] || 'Untitled');

  const abstract = abstractSection?.text || '';

  // Year detection
  const yearMatch = text.match(/(?:19|20)\d{2}/g);
  const years = yearMatch ? [...new Set(yearMatch)].map(Number).sort((a, b) => b - a) : [];
  const pubYear = years.find(y => y >= 1990 && y <= 2030) || null;

  // Word / page estimates
  const words = text.split(/\s+/).filter(Boolean).length;
  const sentences = text.split(/[.!?]+\s+/g).filter(s => s.trim().length > 10).length;
  const paragraphs = text.split(/\n\s*\n+/).filter(Boolean).length;

  // Reading time (avg 250 words/min for technical content)
  const readingTimeMin = Math.ceil(words / 200);

  return {
    title: title.substring(0, 200),
    abstract: abstract.substring(0, 2000),
    pubYear,
    wordCount: words,
    sentenceCount: sentences,
    paragraphCount: paragraphs,
    sectionCount: sections.length,
    readingTimeMin,
    estimatedPages: Math.ceil(words / 300),
  };
}

// ── Citation Extraction ───────────────────────────────────────────────────────
// Extracts references from the references section + inline citations.

const INLINE_CITE_BRACKET = /\[(\d+(?:\s*[,;–-]\s*\d+)*)\]/g;
const INLINE_CITE_AUTHOR = /\(([A-Z][a-z]+(?:\s+(?:et\s+al\.?|and|&)\s+[A-Z][a-z]+)?,?\s*\d{4})\)/g;
const REF_LINE = /^\[(\d+)\]\s+(.+)/;
const DOI_PATTERN = /(?:doi[:\s]*|https?:\/\/doi\.org\/)?(10\.\d{4,}\/[^\s,]+)/gi;

export function extractCitations(text, sections) {
  const refSection = sections.find(s => s.type === 'references');
  const references = [];

  if (refSection) {
    const refLines = refSection.lines || [];
    let currentRef = null;

    for (const line of refLines) {
      const m = line.match(REF_LINE);
      if (m) {
        if (currentRef) references.push(currentRef);
        currentRef = { id: parseInt(m[1]), raw: m[2], authors: '', title: '', year: null };
        // Try to extract year
        const ym = m[2].match(/\((\d{4})\)/);
        if (ym) currentRef.year = parseInt(ym[1]);
        // Try to split author/title (heuristic: everything before first period is author)
        const parts = m[2].split(/\.\s+/);
        if (parts.length >= 2) {
          currentRef.authors = parts[0].trim();
          currentRef.title = parts[1]?.trim() || '';
        }
      } else if (currentRef) {
        currentRef.raw += ' ' + line;
      }
    }
    if (currentRef) references.push(currentRef);
  }

  // Inline citation frequency
  const inlineCites = [];
  let match;
  while ((match = INLINE_CITE_BRACKET.exec(text)) !== null) {
    const nums = match[1].split(/[,;–-]/).map(n => parseInt(n.trim())).filter(n => !isNaN(n));
    inlineCites.push(...nums);
  }
  INLINE_CITE_BRACKET.lastIndex = 0;

  const authorCites = [];
  while ((match = INLINE_CITE_AUTHOR.exec(text)) !== null) {
    authorCites.push(match[1]);
  }
  INLINE_CITE_AUTHOR.lastIndex = 0;

  // Citation frequency map
  const citeFreq = {};
  for (const n of inlineCites) {
    citeFreq[n] = (citeFreq[n] || 0) + 1;
  }

  // DOIs
  const dois = [];
  while ((match = DOI_PATTERN.exec(text)) !== null) {
    dois.push(match[1]);
  }
  DOI_PATTERN.lastIndex = 0;

  return {
    references,
    totalRefs: references.length,
    inlineCitations: inlineCites.length,
    authorCitations: authorCites,
    citationFrequency: citeFreq,
    mostCited: Object.entries(citeFreq).sort((a, b) => b[1] - a[1]).slice(0, 10),
    dois: [...new Set(dois)],
  };
}

// ── Concept Extraction ────────────────────────────────────────────────────────
// Extracts key terms via TF-IDF-like scoring without external libraries.

const STOP_WORDS = new Set([
  'the','a','an','and','or','but','in','on','at','to','for','of','with','by','from',
  'up','about','into','over','after','is','are','was','were','be','been','being',
  'have','has','had','do','does','did','will','would','shall','should','may','might',
  'can','could','this','that','these','those','it','its','i','we','they','he','she',
  'you','me','us','them','my','our','your','their','his','her','which','who','whom',
  'what','where','when','how','why','if','then','else','than','so','not','no','nor',
  'as','such','each','every','all','both','few','more','most','other','some','any',
  'also','just','very','too','only','own','same','new','used','one','two','three',
  'first','second','last','using','based','between','different','through','however',
  'while','since','during','before','after','many','much','et','al','fig','figure',
  'table','section','paper','show','shows','shown','see','given','well','thus',
  'therefore','hence','proposed','approach','method','results','data','model','work',
]);

function tokenizeText(text) {
  return text.toLowerCase()
    .replace(/[^a-z\s-]/g, ' ')
    .split(/\s+/)
    .filter(w => w.length > 2 && !STOP_WORDS.has(w));
}

function extractNgrams(tokens, n) {
  const grams = {};
  for (let i = 0; i <= tokens.length - n; i++) {
    const gram = tokens.slice(i, i + n).join(' ');
    // Filter out grams with stop words at edges
    const parts = gram.split(' ');
    if (STOP_WORDS.has(parts[0]) || STOP_WORDS.has(parts[parts.length - 1])) continue;
    grams[gram] = (grams[gram] || 0) + 1;
  }
  return grams;
}

export function extractConcepts(text, sections) {
  const tokens = tokenizeText(text);
  const totalTokens = tokens.length || 1;

  // Unigram frequencies
  const unigrams = {};
  for (const t of tokens) unigrams[t] = (unigrams[t] || 0) + 1;

  // Bigram and trigram frequencies
  const bigrams = extractNgrams(tokens, 2);
  const trigrams = extractNgrams(tokens, 3);

  // Score: frequency * inverse-document-generality
  // Terms that appear in title/abstract get boosted
  const abstract = sections.find(s => s.type === 'abstract')?.text?.toLowerCase() || '';
  const titleArea = sections.find(s => s.type === 'preamble')?.text?.toLowerCase() || '';

  function scoreTerm(term, freq) {
    let score = freq / totalTokens * 1000; // base TF
    if (abstract.includes(term)) score *= 2.5;
    if (titleArea.includes(term)) score *= 3.0;
    // Penalize very common academic terms
    if (['learning', 'network', 'training', 'neural', 'algorithm', 'function', 'system', 'problem', 'input', 'output', 'layer', 'performance', 'accuracy', 'loss'].includes(term)) {
      score *= 0.4;
    }
    return +score.toFixed(4);
  }

  const uniScored = Object.entries(unigrams)
    .map(([term, freq]) => ({ term, freq, score: scoreTerm(term, freq), type: 'unigram' }))
    .filter(t => t.freq >= 3)
    .sort((a, b) => b.score - a.score);

  const biScored = Object.entries(bigrams)
    .map(([term, freq]) => ({ term, freq, score: scoreTerm(term, freq) * 1.8, type: 'bigram' }))
    .filter(t => t.freq >= 2)
    .sort((a, b) => b.score - a.score);

  const triScored = Object.entries(trigrams)
    .map(([term, freq]) => ({ term, freq, score: scoreTerm(term, freq) * 2.5, type: 'trigram' }))
    .filter(t => t.freq >= 2)
    .sort((a, b) => b.score - a.score);

  // Merge and deduplicate (prioritize longer n-grams)
  const allTerms = [...triScored.slice(0, 30), ...biScored.slice(0, 40), ...uniScored.slice(0, 50)];
  const seen = new Set();
  const concepts = [];
  for (const t of allTerms) {
    if (seen.has(t.term)) continue;
    // Skip if this unigram is part of an already-added bigram/trigram
    if (t.type === 'unigram' && concepts.some(c => c.term.includes(t.term) && c.type !== 'unigram')) continue;
    seen.add(t.term);
    concepts.push(t);
  }

  // Co-occurrence matrix (top 20 concepts)
  const topTerms = concepts.slice(0, 20).map(c => c.term);
  const cooccurrence = buildCooccurrenceMatrix(text, topTerms);

  return {
    concepts: concepts.slice(0, 60),
    topUnigrams: uniScored.slice(0, 20),
    topBigrams: biScored.slice(0, 15),
    topTrigrams: triScored.slice(0, 10),
    cooccurrence,
    vocabularySize: Object.keys(unigrams).length,
    totalTokens,
  };
}

function buildCooccurrenceMatrix(text, terms) {
  const sentences = text.toLowerCase().split(/[.!?]+\s+/g);
  const matrix = {};
  for (const t of terms) matrix[t] = {};

  for (const sent of sentences) {
    const present = terms.filter(t => sent.includes(t));
    for (let i = 0; i < present.length; i++) {
      for (let j = i + 1; j < present.length; j++) {
        matrix[present[i]][present[j]] = (matrix[present[i]][present[j]] || 0) + 1;
        matrix[present[j]][present[i]] = (matrix[present[j]][present[i]] || 0) + 1;
      }
    }
  }
  return matrix;
}

// ── Methodology Detection ─────────────────────────────────────────────────────
// Classifies research methodology via keyword taxonomy.

const METHOD_TAXONOMY = {
  'Supervised Learning': ['classification', 'regression', 'labeled data', 'supervised', 'cross-validation', 'training set', 'test set', 'ground truth'],
  'Unsupervised Learning': ['clustering', 'unsupervised', 'dimensionality reduction', 'auto-encoder', 'self-supervised', 'contrastive learning'],
  'Deep Learning': ['neural network', 'deep learning', 'convolutional', 'recurrent', 'transformer', 'attention mechanism', 'backpropagation', 'gradient descent', 'batch normalization', 'dropout'],
  'Reinforcement Learning': ['reinforcement learning', 'reward', 'policy gradient', 'q-learning', 'markov decision', 'exploration', 'exploitation', 'agent', 'environment'],
  'Natural Language Processing': ['tokenization', 'embedding', 'language model', 'bert', 'gpt', 'sentiment', 'named entity', 'text classification', 'seq2seq', 'word2vec'],
  'Computer Vision': ['image classification', 'object detection', 'segmentation', 'feature extraction', 'convolution', 'pooling', 'bounding box', 'pixel'],
  'Generative Models': ['generative', 'gan', 'variational', 'diffusion', 'auto-regressive', 'sampling', 'latent space', 'decoder', 'generation'],
  'Statistical Analysis': ['hypothesis test', 'p-value', 'confidence interval', 'anova', 'correlation', 'regression analysis', 'bayesian', 'probability distribution', 'chi-square'],
  'Optimization': ['optimization', 'gradient', 'loss function', 'convergence', 'adam', 'sgd', 'learning rate', 'hyperparameter', 'grid search', 'bayesian optimization'],
  'Transfer Learning': ['pre-trained', 'fine-tuning', 'transfer learning', 'domain adaptation', 'few-shot', 'zero-shot'],
  'Graph Methods': ['graph neural', 'node embedding', 'adjacency', 'graph convolution', 'message passing', 'knowledge graph'],
  'Experimental Design': ['ablation study', 'baseline', 'benchmark', 'comparison', 'control group', 'randomized', 'cross-validation', 'holdout'],
};

export function detectMethodology(text) {
  const lower = text.toLowerCase();
  const detected = [];

  for (const [method, keywords] of Object.entries(METHOD_TAXONOMY)) {
    const matches = keywords.filter(kw => lower.includes(kw));
    if (matches.length >= 2) {
      detected.push({
        method,
        confidence: Math.min(matches.length / keywords.length, 1),
        matchedKeywords: matches,
        keywordCount: matches.length,
        totalKeywords: keywords.length,
      });
    }
  }

  detected.sort((a, b) => b.confidence - a.confidence);

  // Detect specific model architectures
  const architectures = detectArchitectures(lower);

  // Detect datasets mentioned
  const datasets = detectDatasets(lower);

  // Detect evaluation metrics
  const metrics = detectMetrics(lower);

  return {
    methods: detected,
    primaryMethod: detected[0]?.method || 'Unknown',
    architectures,
    datasets,
    metrics,
    isEmpirical: detected.some(d => d.method === 'Experimental Design'),
    isTheoretical: lower.includes('theorem') || lower.includes('proof') || lower.includes('lemma'),
  };
}

function detectArchitectures(text) {
  const archs = [
    'resnet', 'vgg', 'inception', 'efficientnet', 'mobilenet', 'densenet',
    'bert', 'gpt', 'roberta', 'xlnet', 'albert', 't5', 'llama', 'mistral',
    'transformer', 'lstm', 'gru', 'cnn', 'rnn', 'autoencoder', 'vae', 'gan',
    'unet', 'yolo', 'faster r-cnn', 'ssd', 'detr',
    'attention', 'self-attention', 'multi-head attention',
    'diffusion model', 'stable diffusion', 'dall-e',
    'graph attention', 'gcn', 'graphsage',
  ];
  return archs.filter(a => text.includes(a)).map(a => ({
    name: a.toUpperCase(),
    mentions: (text.match(new RegExp(a.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi')) || []).length,
  }));
}

function detectDatasets(text) {
  const ds = [
    'imagenet', 'cifar', 'mnist', 'coco', 'pascal voc', 'cityscapes',
    'glue', 'superglue', 'squad', 'imdb', 'yelp', 'amazon reviews',
    'wikitext', 'penn treebank', 'common crawl', 'c4',
    'librispeech', 'audioset', 'voxceleb',
    'ogb', 'planetoid', 'cora', 'citeseer', 'pubmed',
    'kaggle', 'uci', 'openml',
  ];
  return ds.filter(d => text.includes(d)).map(d => ({
    name: d.charAt(0).toUpperCase() + d.slice(1),
    mentions: (text.match(new RegExp(d.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi')) || []).length,
  }));
}

function detectMetrics(text) {
  const mets = [
    { name: 'Accuracy', rx: /accuracy/gi },
    { name: 'Precision', rx: /precision/gi },
    { name: 'Recall', rx: /recall/gi },
    { name: 'F1 Score', rx: /f1[\s-]?score/gi },
    { name: 'AUC-ROC', rx: /auc[\s-]?roc|roc[\s-]?auc/gi },
    { name: 'BLEU', rx: /bleu/gi },
    { name: 'ROUGE', rx: /rouge/gi },
    { name: 'Perplexity', rx: /perplexity/gi },
    { name: 'MSE/RMSE', rx: /(?:r?mse|mean\s+squared\s+error)/gi },
    { name: 'MAE', rx: /(?:mae|mean\s+absolute\s+error)/gi },
    { name: 'IoU', rx: /(?:iou|intersection\s+over\s+union)/gi },
    { name: 'mAP', rx: /(?:map|mean\s+average\s+precision)/gi },
    { name: 'FID', rx: /(?:fid|fréchet\s+inception)/gi },
    { name: 'Loss', rx: /(?:cross[\s-]?entropy|loss\s+function)/gi },
    { name: 'R²', rx: /r[\s-]?squared|r²/gi },
  ];
  return mets.filter(m => m.rx.test(text)).map(m => {
    m.rx.lastIndex = 0;
    const matches = text.match(m.rx);
    return { name: m.name, mentions: matches ? matches.length : 0 };
  });
}

// ── Statistical Claims Detection ──────────────────────────────────────────────
// Finds numerical claims, percentages, comparisons in the text.

export function detectClaims(text) {
  const claims = [];

  // Percentage claims: "achieves 95.3% accuracy"
  const pctRx = /(?:achieve[s]?|obtain[s]?|reach(?:es|ed)?|improve[s]?|outperform[s]?|yield[s]?|report[s]?)[^.]*?(\d+\.?\d*)\s*%/gi;
  let m;
  while ((m = pctRx.exec(text)) !== null) {
    const ctx = text.substring(Math.max(0, m.index - 50), Math.min(text.length, m.index + m[0].length + 50));
    claims.push({ type: 'percentage', value: parseFloat(m[1]), context: ctx.trim(), position: m.index });
  }

  // Comparison claims: "X% improvement over", "outperforms ... by"
  const compRx = /(?:improvement|gain|reduction|decrease|increase)\s+(?:of\s+)?(\d+\.?\d*)\s*%/gi;
  while ((m = compRx.exec(text)) !== null) {
    const ctx = text.substring(Math.max(0, m.index - 50), Math.min(text.length, m.index + m[0].length + 50));
    claims.push({ type: 'comparison', value: parseFloat(m[1]), context: ctx.trim(), position: m.index });
  }

  // Numerical results: "F1 score of 0.923"
  const numRx = /(?:score|accuracy|precision|recall|f1|bleu|rouge|perplexity|loss|error)\s+(?:of\s+)?(\d+\.?\d+)/gi;
  while ((m = numRx.exec(text)) !== null) {
    const ctx = text.substring(Math.max(0, m.index - 40), Math.min(text.length, m.index + m[0].length + 40));
    claims.push({ type: 'metric', value: parseFloat(m[1]), context: ctx.trim(), position: m.index });
  }

  // State-of-the-art claims
  const sotaRx = /state[\s-]of[\s-]the[\s-]art|SOTA|new\s+record|best[\s-]performing|surpass(?:es|ed)/gi;
  while ((m = sotaRx.exec(text)) !== null) {
    const ctx = text.substring(Math.max(0, m.index - 60), Math.min(text.length, m.index + m[0].length + 60));
    claims.push({ type: 'sota_claim', value: null, context: ctx.trim(), position: m.index });
  }

  claims.sort((a, b) => a.position - b.position);
  return {
    claims,
    totalClaims: claims.length,
    percentageClaims: claims.filter(c => c.type === 'percentage').length,
    comparisonClaims: claims.filter(c => c.type === 'comparison').length,
    sotaClaims: claims.filter(c => c.type === 'sota_claim').length,
    metricClaims: claims.filter(c => c.type === 'metric').length,
  };
}

// ── Readability & Complexity ──────────────────────────────────────────────────
export function analyzeReadability(text) {
  const sentences = text.split(/[.!?]+\s+/g).filter(s => s.trim().length > 5);
  const words = text.split(/\s+/).filter(Boolean);
  const syllableCount = words.reduce((sum, w) => sum + countSyllables(w), 0);

  const totalSentences = Math.max(sentences.length, 1);
  const totalWords = Math.max(words.length, 1);

  // Flesch Reading Ease
  const flesch = 206.835 - 1.015 * (totalWords / totalSentences) - 84.6 * (syllableCount / totalWords);

  // Flesch-Kincaid Grade Level
  const fkGrade = 0.39 * (totalWords / totalSentences) + 11.8 * (syllableCount / totalWords) - 15.59;

  // Avg sentence length
  const avgSentenceLen = totalWords / totalSentences;

  // Vocabulary richness (type-token ratio)
  const uniqueWords = new Set(words.map(w => w.toLowerCase().replace(/[^a-z]/g, ''))).size;
  const ttr = uniqueWords / totalWords;

  // Long word ratio (>= 7 chars)
  const longWords = words.filter(w => w.length >= 7).length;
  const longWordRatio = longWords / totalWords;

  // Technical density: ratio of domain-specific terms
  const techTerms = ['algorithm', 'optimization', 'convergence', 'gradient', 'embedding',
    'latent', 'parameter', 'hyperparameter', 'regularization', 'normalization',
    'architecture', 'inference', 'propagation', 'convolution', 'recurrent',
    'stochastic', 'probabilistic', 'distribution', 'variance', 'covariance',
    'eigen', 'matrix', 'tensor', 'vector', 'decomposition'];
  const techCount = words.filter(w => techTerms.some(t => w.toLowerCase().includes(t))).length;
  const techDensity = techCount / totalWords;

  // Complexity score (0-100, higher = more complex)
  const complexity = Math.min(100, Math.max(0,
    (fkGrade / 20) * 30 +
    longWordRatio * 100 * 0.2 +
    techDensity * 1000 * 0.3 +
    (1 - ttr) * 0.2 * 100
  ));

  return {
    fleschReadingEase: +flesch.toFixed(1),
    fleschKincaidGrade: +fkGrade.toFixed(1),
    avgSentenceLength: +avgSentenceLen.toFixed(1),
    avgWordLength: +(words.reduce((s, w) => s + w.length, 0) / totalWords).toFixed(1),
    vocabularyRichness: +ttr.toFixed(3),
    longWordRatio: +(longWordRatio * 100).toFixed(1),
    technicalDensity: +(techDensity * 100).toFixed(2),
    complexityScore: +complexity.toFixed(0),
    syllablesPerWord: +(syllableCount / totalWords).toFixed(2),
    totalSentences,
    totalWords,
    gradeLabel: gradeLabel(fkGrade),
    difficultyLabel: flesch > 60 ? 'Accessible' : flesch > 30 ? 'Academic' : 'Highly Technical',
  };
}

function countSyllables(word) {
  const w = word.toLowerCase().replace(/[^a-z]/g, '');
  if (w.length <= 2) return 1;
  let count = 0;
  const vowels = 'aeiouy';
  let prevVowel = false;
  for (const ch of w) {
    const isV = vowels.includes(ch);
    if (isV && !prevVowel) count++;
    prevVowel = isV;
  }
  if (w.endsWith('e') && count > 1) count--;
  return Math.max(count, 1);
}

function gradeLabel(grade) {
  if (grade < 8) return 'General';
  if (grade < 12) return 'Undergraduate';
  if (grade < 16) return 'Graduate';
  if (grade < 20) return 'PhD-level';
  return 'Expert-only';
}

// ── Reproducibility Score ─────────────────────────────────────────────────────
// Computational scoring of how reproducible a paper's work is.

export function scoreReproducibility(text, sections, methodology, citations) {
  let score = 0;
  const factors = [];

  // Has methodology section?
  const hasMethod = sections.some(s => s.type === 'methodology');
  if (hasMethod) { score += 15; factors.push({ label: 'Methodology section present', pts: 15 }); }

  // Has experiments section?
  const hasExp = sections.some(s => s.type === 'experiments');
  if (hasExp) { score += 10; factors.push({ label: 'Experiments section present', pts: 10 }); }

  // Code availability
  const hasCode = /github\.com|code\s+(?:is\s+)?(?:available|released|open[\s-]?source)|our\s+(?:implementation|code)/i.test(text);
  if (hasCode) { score += 20; factors.push({ label: 'Code availability mentioned', pts: 20 }); }

  // Dataset description
  const hasDataDesc = /dataset\s+(?:contains|consists|comprises)|training\s+(?:data|set)|we\s+(?:collected|used|gathered)/i.test(text);
  if (hasDataDesc) { score += 10; factors.push({ label: 'Dataset described', pts: 10 }); }

  // Hyperparameter details
  const hasHyperparams = /learning\s+rate|batch\s+size|epochs?|hidden\s+(?:units|layers|size)|dropout\s+(?:rate|ratio)/i.test(text);
  if (hasHyperparams) { score += 15; factors.push({ label: 'Hyperparameters specified', pts: 15 }); }

  // Baseline comparisons
  const hasBaselines = /baseline|compared?\s+(?:with|to|against)|state[\s-]of[\s-]the[\s-]art/i.test(text);
  if (hasBaselines) { score += 10; factors.push({ label: 'Baseline comparisons', pts: 10 }); }

  // Ablation study
  const hasAblation = /ablation\s+study|ablation\s+experiment|we\s+(?:remove|ablate)/i.test(text);
  if (hasAblation) { score += 10; factors.push({ label: 'Ablation study present', pts: 10 }); }

  // Statistical significance
  const hasSigTest = /p[\s-]?value|statistical(?:ly)?\s+significant|confidence\s+interval|standard\s+deviation|±/i.test(text);
  if (hasSigTest) { score += 10; factors.push({ label: 'Statistical significance reported', pts: 10 }); }

  return {
    score: Math.min(score, 100),
    maxScore: 100,
    factors,
    label: score >= 80 ? 'Highly Reproducible' : score >= 50 ? 'Moderately Reproducible' : score >= 30 ? 'Partially Reproducible' : 'Low Reproducibility',
  };
}

// ── Full Paper Analysis Pipeline ──────────────────────────────────────────────
export function analyzePaper(text) {
  const sections = detectSections(text);
  const metadata = extractMetadata(text, sections);
  const concepts = extractConcepts(text, sections);
  const citations = extractCitations(text, sections);
  const methodology = detectMethodology(text);
  const claims = detectClaims(text);
  const readability = analyzeReadability(text);
  const reproducibility = scoreReproducibility(text, sections, methodology, citations);

  return {
    metadata,
    sections,
    concepts,
    citations,
    methodology,
    claims,
    readability,
    reproducibility,
  };
}
